import { User, Org } from "types";
import { DEMO_ORG_ID } from "@constants/branding";

export function isGUEST(userData?: User | null): boolean {
  return userData?.config?.access_type === "GUEST";
}

/**
 * Firestore id of the MBMO (production/admin) organisation.
 *
 * There are only two orgs in the system: MBMO and the dev/demo org
 * (`DEMO_ORG_ID`, "HEXAFLOW"). Neither carries an `org.type`, so these pages
 * are gated by org id — same explicit-id pattern as `isDevOrg`/`DEMO_ORG_ID`.
 */
export const MBMO_ORG_ID = "c5ME2S7KEDS3LMBK2ht4";

/**
 * The admin "Invitations & clients" page (relink / client monitoring) is
 * reserved for members whose organisational role is Administrateur
 * (`role === "admin"`, see `role_dict`) within either the MBMO org or the
 * dev/demo org.
 *
 * This is intentionally separate from the per-org `config.admin` flag that
 * gates the other Admin-section pages (Utilisateurs, Algolia).
 */
export function canSeeAdminInvitations(
  userData?: User | null,
  orgData?: Org | null,
): boolean {
  if (userData?.role !== "admin") return false;
  if (!orgData) return false;
  return orgData.id === MBMO_ORG_ID || orgData.id === DEMO_ORG_ID;
}

export function isADMIN(userData?: User | null): boolean {
  return userData?.config?.access_type === "ADMIN" || userData?.config?.access_type === "OWNER";
}

/**
 * Owners / admins / members with `can_view_all_projects` see everything
 * regardless of team membership. Anyone else is scoped to projects they
 * can actually see.
 *
 * Used by Notifications + Boîte de réception screens as a frontend
 * belt-and-braces filter on top of the existing backend scoping.
 */
export function canSeeAllProjects(userData?: User | null): boolean {
  if (!userData) return false;
  if (isADMIN(userData)) return true;
  return !!userData.config?.can_view_all_projects;
}

/**
 * For non-privileged users, return the set of project ids they can see —
 * i.e. projects already loaded into the project store, which is itself
 * scoped server-side (`/projects` enforces team + guest-of access).
 *
 * Pass the current `projects` array from `useProjectStore`. The result is
 * meant to be used with `Set.has(...)` to filter list views.
 */
export function accessibleProjectIdsFor(
  userData: User | null | undefined,
  projects: { id: string }[],
): Set<string> | "all" {
  if (canSeeAllProjects(userData)) return "all";
  return new Set(projects.map((p) => p.id));
}

/**
 * Suivi salariés (employee activity dashboard) is admin-only by default,
 * but can be opened up to a specific member via the
 * ``can_view_suivi_salaries`` config flag — same opt-in pattern as
 * ``can_view_all_projects`` for trusted assistants/HR-style profiles.
 */
export function canSeeSuiviSalaries(userData?: User | null): boolean {
  if (!userData) return false;
  if (isADMIN(userData)) return true;
  return !!userData.config?.can_view_suivi_salaries;
}

/**
 * Dessinateur (viewer role): internal staff with read-only access to most
 * project data. They can view projects but cannot validate control points,
 * and don't have access to the Artisans or Facturation sections.
 */
export function isDESSINATEUR(userData?: User | null): boolean {
  return userData?.role === "viewer";
}

/**
 * Commercial role: internal staff focused on client relationship and
 * estimates. They don't need access to billing — that's for admins,
 * conductors and the comptability role.
 */
export function isCOMMERCIAL(userData?: User | null): boolean {
  return userData?.role === "commercial";
}

/**
 * Conducteur de travaux role.
 */
export function isCONDUCTOR(userData?: User | null): boolean {
  return userData?.role === "conductor";
}

/**
 * Roles that must never see the Facturation tab inside a project.
 * Used by both web and mobile to gate the tab.
 *
 * Admins/owners always see everything regardless of their `role`.
 */
export function cannotSeeFacturation(userData?: User | null): boolean {
  if (isADMIN(userData)) return false;
  return isDESSINATEUR(userData) || isCOMMERCIAL(userData);
}

/**
 * Roles that get a stripped-down Settings sidebar:
 * no Equipe page, no Debug section. Used by both web and mobile.
 *
 * Admins/owners always see everything regardless of their `role`.
 */
export function hasRestrictedSettings(userData?: User | null): boolean {
  if (isADMIN(userData)) return false;
  return (
    isCOMMERCIAL(userData) || isCONDUCTOR(userData) || isDESSINATEUR(userData)
  );
}
